/**
 * Create / edit dialog for an IngressSource.
 *
 * The dialog renders three sub-forms:
 *
 *   1. Adapter type (create only) + name
 *   2. Adapter config — auto-rendered from the JSON Schema returned by
 *      GET /ingress/types (one entry per registered adapter).
 *   3. Format — declared payload format, parsed by the backend's
 *      parser registry (json/xml/csv/txt/binary/auto). Hidden when the
 *      adapter type forces a format (db_poll → json).
 *
 * The ingress source is wired to workflows by editing each workflow's
 * SOURCE node and pointing it at this source — there is no
 * target_workflow_id field anymore (inverted ownership).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { IngressConfigForm } from "@/components/ingress/IngressConfigForm";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ingressApi,
  type FormatSpec,
  type IngressSource,
  type IngressType,
} from "@/lib/api-ingress";

export type DialogMode =
  | { kind: "create"; defaultType?: IngressType }
  | { kind: "edit"; source: IngressSource };

export interface IngressSourceDialogProps {
  open: boolean;
  mode: DialogMode;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_FORMAT: FormatSpec = { type: "auto" };

export function IngressSourceDialog({ open, mode, onOpenChange }: IngressSourceDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const seed = mode.kind === "edit" ? mode.source : null;

  const typesQuery = useQuery({
    queryKey: ["ingress", "types"],
    queryFn: () => ingressApi.types(),
    enabled: open,
  });

  const [type, setType] = React.useState<IngressType>(
    seed?.type ?? (mode.kind === "create" ? mode.defaultType ?? "webhook" : "webhook"),
  );
  const [name, setName] = React.useState(seed?.name ?? "");
  const [enabled, setEnabled] = React.useState(seed?.enabled ?? true);
  const [config, setConfig] = React.useState<Record<string, unknown>>(seed?.config ?? {});
  const [format, setFormat] = React.useState<FormatSpec>(seed?.format ?? DEFAULT_FORMAT);
  const [description, setDescription] = React.useState(seed?.description ?? "");
  const [tags, setTags] = React.useState<string[]>(seed?.tags ?? []);

  React.useEffect(() => {
    if (open) {
      setType(seed?.type ?? (mode.kind === "create" ? mode.defaultType ?? "webhook" : "webhook"));
      setName(seed?.name ?? "");
      setEnabled(seed?.enabled ?? true);
      setConfig(seed?.config ?? {});
      setFormat(seed?.format ?? DEFAULT_FORMAT);
      setDescription(seed?.description ?? "");
      setTags(seed?.tags ?? []);
    }
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // When switching type during create, reset config + format. db_poll
  // forces json — set it up front so the user sees what they'll get.
  const previousType = React.useRef(type);
  React.useEffect(() => {
    if (isCreate && previousType.current !== type) {
      setConfig({});
      setFormat(type === "db_poll" ? { type: "json" } : DEFAULT_FORMAT);
    }
    previousType.current = type;
  }, [type, isCreate]);

  const adapterInfo = React.useMemo(
    () => typesQuery.data?.find((t) => t.type === type),
    [typesQuery.data, type],
  );
  const schema = adapterInfo?.config_schema ?? { properties: {}, required: [] };
  const formatForced = adapterInfo?.format_forced ?? false;
  const supportedFormats = adapterInfo?.supported_formats ?? [
    "auto",
    "json",
    "xml",
    "csv",
    "txt",
    "binary",
  ];

  const mutation = useMutation({
    mutationFn: () => {
      if (isCreate) {
        return ingressApi.create({
          type,
          name: name.trim(),
          enabled,
          config,
          format,
          description: description.trim() || null,
          tags,
        });
      }
      return ingressApi.update(seed!.id, {
        enabled,
        config,
        format,
        description: description.trim() || null,
        tags,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingress"] });
      onOpenChange(false);
    },
  });

  const valid = /^[a-zA-Z0-9._-]+$/.test(name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate ? "New ingress source" : `Edit ${seed?.name}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isCreate && (
            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="flex flex-wrap gap-2">
                {(typesQuery.data ?? []).map((t) => (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setType(t.type)}
                    className={
                      "px-3 h-7 text-xs uppercase tracking-wider border " +
                      (type === t.type
                        ? "bg-accent text-accent-foreground border-accent"
                        : "border-border hover:bg-muted text-muted-foreground")
                    }
                  >
                    {t.type}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ing-name">Name</Label>
            <Input
              id="ing-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isCreate}
              placeholder={
                type === "webhook" ? "erp.mexal" : type === "sftp_pull" ? "vendor.daily" : "source-name"
              }
              className="font-mono"
            />
            {type === "webhook" && (
              <p className="text-xs text-muted-foreground">
                Inbound URL: <span className="font-mono">POST /api/webhooks/{name || "<name>"}</span>
              </p>
            )}
          </div>

          <div className="border-t border-border/60 pt-3">
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
              Adapter config
            </div>
            {typesQuery.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <IngressConfigForm schema={schema as any} value={config} onChange={setConfig} />
            )}
          </div>

          {!formatForced && (
            <div className="border-t border-border/60 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                Payload format
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ing-format-type">Type</Label>
                <select
                  id="ing-format-type"
                  className="block w-full border border-border bg-background px-2 py-1.5 text-xs"
                  value={format.type}
                  onChange={(e) => setFormat({ type: e.target.value })}
                >
                  {supportedFormats.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Parsed by the backend before reaching the workflow.{" "}
                  <span className="font-mono">auto</span> sniffs the Content-Type / extension.
                </p>
              </div>
              <FormatSubForm format={format} onChange={setFormat} />
            </div>
          )}

          {formatForced && (
            <Alert tone="info" title="Format">
              This adapter always emits structured rows ({" "}
              <span className="font-mono">{format.type}</span> ) — the
              format selector is hidden.
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ing-desc">Description</Label>
            <Input
              id="ing-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this source for?"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ing-tags">Tags</Label>
            <TagsField
              id="ing-tags"
              value={tags}
              onChange={setTags}
              scope="ingress_sources"
              placeholder="e.g. customer:acme, env:prod"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span>Enabled</span>
          </label>

          {mutation.error && (
            <Alert tone="danger" title="Failed">
              {(mutation.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            {isCreate ? "Create source" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Per-format sub-config (csv / txt have a few extra knobs)
// ---------------------------------------------------------------------------

function FormatSubForm({
  format,
  onChange,
}: {
  format: FormatSpec;
  onChange: (next: FormatSpec) => void;
}) {
  const cfg = (format.config ?? {}) as Record<string, unknown>;
  const update = (k: string, v: unknown) => {
    const next: Record<string, unknown> = { ...cfg };
    if (v === "" || v === undefined || v === null) {
      delete next[k];
    } else {
      next[k] = v;
    }
    onChange({ type: format.type, config: Object.keys(next).length ? next : undefined });
  };

  if (format.type === "csv") {
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <Label>delimiter</Label>
          <Input
            value={(cfg.delimiter as string) ?? ","}
            onChange={(e) => update("delimiter", e.target.value)}
          />
        </div>
        <div>
          <Label>quote_char</Label>
          <Input
            value={(cfg.quote_char as string) ?? '"'}
            onChange={(e) => update("quote_char", e.target.value)}
          />
        </div>
        <div>
          <Label>encoding</Label>
          <Input
            value={(cfg.encoding as string) ?? "utf-8"}
            onChange={(e) => update("encoding", e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 self-end text-xs">
          <input
            type="checkbox"
            checked={cfg.header !== false}
            onChange={(e) => update("header", e.target.checked)}
          />
          <span>first row is header</span>
        </label>
      </div>
    );
  }

  if (format.type === "txt") {
    return (
      <div className="mt-2">
        <Label>encoding</Label>
        <Input
          value={(cfg.encoding as string) ?? "utf-8"}
          onChange={(e) => update("encoding", e.target.value)}
        />
      </div>
    );
  }

  return null;
}
